import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingSkeleton } from '@/components/ui/loading-spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Phone, User, Calendar, FileText, MessageSquare, Star, CalendarDays, UserPlus, FileSignature } from 'lucide-react';
import InterviewScheduler from './InterviewScheduler';
import OnboardingWorkflowManager from './OnboardingWorkflowManager';
import ContractGenerationDialog from './ContractGenerationDialog';

interface JobApplication {
  id: string;
  job_posting_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  work_experience: string | null;
  education: string | null;
  skills: string | null;
  availability: string | null;
  status: string;
  application_source: string | null;
  internal_notes: string | null;
  screening_score: number | null;
  interview_score: number | null;
  overall_score: number | null;
  submitted_at: string;
  source_tag_id: string | null;
  // QR code info
  qr_code_name?: string;
}

interface JobApplicationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobPostingId: string;
  jobTitle: string;
  businessId: string;
}

const JobApplicationsModal: React.FC<JobApplicationsModalProps> = ({
  isOpen,
  onClose,
  jobPostingId,
  jobTitle,
  businessId
}) => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isInterviewSchedulerOpen, setIsInterviewSchedulerOpen] = useState(false);
  const [isOnboardingManagerOpen, setIsOnboardingManagerOpen] = useState(false);
  const [isContractGenerationOpen, setIsContractGenerationOpen] = useState(false);
  const [jobPosting, setJobPosting] = useState<any>(null);

  useEffect(() => {
    if (isOpen && jobPostingId) {
      fetchApplications();
      fetchJobPosting();
    }
  }, [isOpen, jobPostingId]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      
      // Fetch applications with QR code information
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          qr_codes!job_applications_source_tag_id_fkey (
            name
          )
        `)
        .eq('job_posting_id', jobPostingId)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include QR code name
      const transformedData = data?.map(app => ({
        ...app,
        qr_code_name: app.qr_codes?.name || null
      })) || [];

      setApplications(transformedData);
    } catch (error: unknown) {
      console.error('Error fetching applications:', error);
      toast.error("Failed to fetch applications.", {
        description: error instanceof Error ? error.message : String(error) || "Please try again"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchJobPosting = async () => {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('id', jobPostingId)
        .single();

      if (error) throw error;
      setJobPosting(data);
    } catch (error) {
      console.error('Error fetching job posting:', error);
    }
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      setUpdatingStatus(true);
      
      const { error } = await supabase
        .from('job_applications')
        .update({ status: newStatus })
        .eq('id', applicationId);

      if (error) throw error;

      toast.success("Application status updated successfully.");
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
      
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(prev => prev ? { ...prev, status: newStatus } : null);
      }

      // If status is "offered" and job posting requires contracts, show contract generation
      if (newStatus === 'offered' && jobPosting?.requires_contract) {
        setIsContractGenerationOpen(true);
      }
    } catch (error: unknown) {
      console.error('Error updating status:', error);
      toast.error("Failed to update status.", {
        description: error instanceof Error ? error.message : String(error) || "Please try again"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateApplicationNotes = async (applicationId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ internal_notes: notes })
        .eq('id', applicationId);

      if (error) throw error;

      toast.success("Notes updated successfully.");
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, internal_notes: notes } : app
        )
      );
      
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(prev => prev ? { ...prev, internal_notes: notes } : null);
      }
    } catch (error: unknown) {
      console.error('Error updating notes:', error);
      toast.error("Failed to update notes.");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'screening': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'interview-scheduled': return 'bg-primary/10 text-primary';
      case 'interviewed': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'offered': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'hired': return 'bg-green-600 text-white';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'withdrawn': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Applications for: {jobTitle}</DialogTitle>
          <DialogDescription>
            Manage and review job applications for this position.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 h-[70vh]">
          {/* Applications List */}
          <div className="w-1/3 border-r pr-4">
            <h3 className="text-lg font-semibold mb-4">
              Applications ({applications.length})
            </h3>
            <ScrollArea className="h-full">
              {loading ? (
                <div className="space-y-4 py-8">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <Skeleton className="h-5 w-32" />
                          <Skeleton className="h-4 w-48" />
                        </div>
                        <Skeleton className="h-6 w-20" />
                      </div>
                      <div className="flex space-x-4">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-28" />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Skeleton className="h-8 w-16" />
                        <Skeleton className="h-8 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-8">
                  <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No applications yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <Card 
                      key={app.id} 
                      className={`cursor-pointer transition-colors ${
                        selectedApplication?.id === app.id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => setSelectedApplication(app)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">
                            {app.first_name} {app.last_name}
                          </h4>
                          <Badge className={getStatusColor(app.status)}>
                            {app.status.replace('-', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{app.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Applied: {formatDate(app.submitted_at)}
                        </p>
                        {app.qr_code_name && (
                          <p className="text-xs text-blue-600 mt-1">
                            Source: {app.qr_code_name}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Application Details */}
          <div className="flex-1">
            {selectedApplication ? (
              <Tabs defaultValue="details" className="h-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="status">Status & Notes</TabsTrigger>
                  <TabsTrigger value="interviews">Interviews</TabsTrigger>
                  <TabsTrigger value="evaluation">Evaluation</TabsTrigger>
                  <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <ScrollArea className="h-[50vh]">
                    <div className="space-y-4">
                      {/* Contact Information */}
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <User className="h-5 w-5" />
                            Contact Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{selectedApplication.email}</span>
                          </div>
                          {selectedApplication.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span>{selectedApplication.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>Applied: {formatDate(selectedApplication.submitted_at)}</span>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Experience & Skills */}
                      {selectedApplication.work_experience && (
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                            <CardTitle className="text-sm font-medium">Work Experience</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="whitespace-pre-wrap">{selectedApplication.work_experience}</p>
                          </CardContent>
                        </Card>
                      )}

                      {selectedApplication.education && (
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                            <CardTitle className="text-sm font-medium">Education</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="whitespace-pre-wrap">{selectedApplication.education}</p>
                          </CardContent>
                        </Card>
                      )}

                      {selectedApplication.skills && (
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                            <CardTitle className="text-sm font-medium">Skills</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="whitespace-pre-wrap">{selectedApplication.skills}</p>
                          </CardContent>
                        </Card>
                      )}

                      {selectedApplication.availability && (
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                            <CardTitle className="text-sm font-medium">Availability</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="whitespace-pre-wrap">{selectedApplication.availability}</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="status" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium">Application Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="status">Current Status</Label>
                        <Select
                          value={selectedApplication.status}
                          onValueChange={(value) => updateApplicationStatus(selectedApplication.id, value)}
                          disabled={updatingStatus}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="submitted">Submitted</SelectItem>
                            <SelectItem value="screening">Screening</SelectItem>
                            <SelectItem value="interview-scheduled">Interview Scheduled</SelectItem>
                            <SelectItem value="interviewed">Interviewed</SelectItem>
                            <SelectItem value="reference-check">Reference Check</SelectItem>
                            <SelectItem value="offered">Offered</SelectItem>
                            <SelectItem value="hired">Hired</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Contract Generation */}
                      {jobPosting?.requires_contract && (selectedApplication.status === 'offered' || selectedApplication.status === 'hired') && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <FileSignature className="h-4 w-4 text-blue-600" />
                            <h4 className="font-medium text-blue-800">Contract Required</h4>
                          </div>
                          <p className="text-sm text-blue-700 mb-3">
                            This position requires a contract. Generate an employment contract for this candidate.
                          </p>
                          <Button 
                            onClick={() => setIsContractGenerationOpen(true)}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <FileSignature className="h-4 w-4 mr-2" />
                            Generate Contract
                          </Button>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="notes">Internal Notes</Label>
                        <Textarea
                          id="notes"
                          value={selectedApplication.internal_notes || ''}
                          onChange={(e) => updateApplicationNotes(selectedApplication.id, e.target.value)}
                          placeholder="Add internal notes about this candidate..."
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="interviews" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CalendarDays className="h-5 w-5" />
                        Interview Management
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-6">
                        <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">
                          Schedule and manage interviews for {selectedApplication.first_name} {selectedApplication.last_name}
                        </p>
                        <Button onClick={() => setIsInterviewSchedulerOpen(true)}>
                          <Calendar className="h-4 w-4 mr-2" />
                          Manage Interviews
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="onboarding" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        Employee Onboarding
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedApplication.status === 'hired' ? (
                        <div className="text-center py-6">
                          <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">
                            Start the onboarding process for {selectedApplication.first_name} {selectedApplication.last_name}
                          </p>
                          <Button onClick={() => setIsOnboardingManagerOpen(true)}>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Manage Onboarding
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">
                            Onboarding is available once the candidate is hired.
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            Current status: <span className="font-medium">{selectedApplication.status}</span>
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="evaluation" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Star className="h-5 w-5" />
                        Evaluation Scores
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <Label>Screening Score</Label>
                          <div className="text-2xl font-bold text-blue-600">
                            {selectedApplication.screening_score || '-'}/100
                          </div>
                        </div>
                        <div className="text-center">
                          <Label>Interview Score</Label>
                          <div className="text-2xl font-bold text-primary">
                            {selectedApplication.interview_score || '-'}/100
                          </div>
                        </div>
                        <div className="text-center">
                          <Label>Overall Score</Label>
                          <div className="text-2xl font-bold text-green-600">
                            {selectedApplication.overall_score || '-'}/100
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center">
                        Scores can be updated during the interview process
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select an application to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Interview Scheduler Modal */}
        <InterviewScheduler
          isOpen={isInterviewSchedulerOpen}
          onClose={() => setIsInterviewSchedulerOpen(false)}
          application={selectedApplication}
          businessId={businessId}
          onInterviewScheduled={() => {
            // Refresh applications to update status
            fetchApplications();
          }}
        />

        {/* Onboarding Workflow Manager Modal */}
        <OnboardingWorkflowManager
          isOpen={isOnboardingManagerOpen}
          onClose={() => setIsOnboardingManagerOpen(false)}
          businessId={businessId}
          selectedApplication={selectedApplication}
        />

        {/* Contract Generation Modal */}
        <ContractGenerationDialog
          isOpen={isContractGenerationOpen}
          onClose={() => {
            setIsContractGenerationOpen(false);
            // Refresh applications to update status
            fetchApplications();
          }}
          businessId={businessId}
          jobApplication={selectedApplication ? {
            id: selectedApplication.id,
            job_posting_id: selectedApplication.job_posting_id,
            applicant_name: `${selectedApplication.first_name} ${selectedApplication.last_name}`,
            applicant_email: selectedApplication.email,
            application_data: selectedApplication
          } : null}
          jobPosting={jobPosting}
        />
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationsModal;